"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.validateKicadNetlist = validateKicadNetlist;
exports.applyKicadNetlistToProject = applyKicadNetlistToProject;
exports.selectKicadNetlistFile = selectKicadNetlistFile;
exports.getSchematicFromResult = getSchematicFromResult;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const kicadNetlistParser_1 = require("./kicadNetlistParser");
const constants_1 = require("./constants");
const applyNetlistToProject_1 = require("../Altium/applyNetlistToProject");
function validateKicadNetlist(filename, fileContents) {
    try {
        const result = (0, kicadNetlistParser_1.parseKicadNetlist)(filename, fileContents);
        return {
            metadata: {
                [constants_1.KicadNetlistSchematicModel]: result.schematic,
            },
            referencedFiles: [],
            version: '1.0',
        };
    }
    catch (error) {
        return {
            skipReason: `Failed to parse KiCad netlist: ${error instanceof Error ? error.message : String(error)}`,
            metadata: {},
            referencedFiles: [],
        };
    }
}
function applyKicadNetlistToProject(projectDefinition, projectModel) {
    const netlistFile = selectKicadNetlistFile(projectDefinition);
    if (!netlistFile) {
        return Promise.resolve({
            type: sigma_engine_shared_1.DesignModels.MigrationResultType.ProjectMigration,
            message: 'No netlist file found',
            innerResults: [],
            isModified: false,
            entity: null,
        });
    }
    const netlistModel = netlistFile.validationResult.data.metadata[constants_1.KicadNetlistSchematicModel];
    const serializedProject = toSerializedProjectModel(projectModel);
    const result = (0, applyNetlistToProject_1.applyNetlistToProject)(serializedProject, resolveKicadNetlistPins(serializedProject, netlistModel));
    return Promise.resolve({
        ...result,
        type: sigma_engine_shared_1.DesignModels.MigrationResultType.ProjectMigration,
    });
}
function resolveKicadNetlistPins(projectModel, netlistModel) {
    const resolvedNetlist = structuredClone(netlistModel);
    const netlistModulesByName = new Map();
    for (const candidate of resolvedNetlist.modules) {
        if (!netlistModulesByName.has(candidate.name)) {
            netlistModulesByName.set(candidate.name, candidate);
        }
    }
    const nodesByModuleAndNumber = new Map();
    for (const [netName, net] of Object.entries(netlistModel.nets)) {
        for (const connection of net.connections) {
            const nodesByNumber = nodesByModuleAndNumber.get(connection.moduleName) ??
                new Map();
            const nodes = nodesByNumber.get(connection.pinRef) ?? [];
            nodes.push({ netName, pinFunction: connection.pinFunction });
            nodesByNumber.set(connection.pinRef, nodes);
            nodesByModuleAndNumber.set(connection.moduleName, nodesByNumber);
        }
    }
    for (const design of Object.values(projectModel.designs)) {
        if (design.modelType !== 'schematic') {
            continue;
        }
        for (const module of design.modules) {
            const netlistModuleName = module.fullModuleName ?? module.name;
            const netlistModule = netlistModulesByName.get(netlistModuleName);
            const component = projectModel.components[module.componentRef];
            const symbol = component?.symbols.find((candidate) => candidate.name === module.footprintRef);
            if (!netlistModule || !symbol) {
                continue;
            }
            const nodesByNumber = nodesByModuleAndNumber.get(netlistModuleName) ?? new Map();
            const resolvedPinNets = { ...netlistModule.pinNets };
            for (const [pinNumber, nodes] of nodesByNumber) {
                const pins = symbol.pins.filter((pin) => pin.number === pinNumber);
                if (pins.length === 0) {
                    continue;
                }
                if (nodes.length === 1) {
                    for (const pin of pins) {
                        resolvedPinNets[pin.ref] = nodes[0].netName;
                    }
                    continue;
                }
                const unmatchedPins = new Set(pins);
                for (const node of nodes) {
                    const matchingPins = node.pinFunction ? pins.filter((pin) => pin.name === node.pinFunction) : [];
                    if (matchingPins.length === 1) {
                        resolvedPinNets[matchingPins[0].ref] = node.netName;
                        unmatchedPins.delete(matchingPins[0]);
                    }
                }
                const unmatchedNodes = nodes.filter((node) => !node.pinFunction || pins.filter((pin) => pin.name === node.pinFunction).length !== 1);
                for (const [index, pin] of [...unmatchedPins].entries()) {
                    const node = unmatchedNodes[index];
                    if (node) {
                        resolvedPinNets[pin.ref] = node.netName;
                    }
                }
            }
            netlistModule.pinNets = resolvedPinNets;
        }
    }
    return resolvedNetlist;
}
function toSerializedProjectModel(projectModel) {
    return JSON.parse(JSON.stringify(projectModel));
}
function selectKicadNetlistFile(projectDefinition) {
    const validNetlists = Object.values(projectDefinition.other ?? {}).filter((f) => f.type === 'netlist' && f.validationResult?.isValid);
    if (validNetlists.length <= 1) {
        return validNetlists[0];
    }
    const projectFile = Object.values(projectDefinition.other ?? {}).find((f) => f.type === 'project' && f.originalFileName.toLowerCase().endsWith('.kicad_pro'));
    const projectBasename = projectFile ? getBasenameWithoutExtension(projectFile.originalFileName).toLowerCase() : undefined;
    const [best] = [...validNetlists].sort((a, b) => compareNetlistCandidates(a, b, projectBasename));
    return best;
}
function compareNetlistCandidates(a, b, projectBasename) {
    const aMatchesProject = matchesProjectBasename(a, projectBasename);
    const bMatchesProject = matchesProjectBasename(b, projectBasename);
    if (aMatchesProject !== bMatchesProject) {
        return aMatchesProject ? -1 : 1;
    }
    const depthDiff = pathDepth(a.originalFileName) - pathDepth(b.originalFileName);
    if (depthDiff !== 0) {
        return depthDiff;
    }
    if (a.originalFileName < b.originalFileName) {
        return -1;
    }
    if (a.originalFileName > b.originalFileName) {
        return 1;
    }
    return 0;
}
function matchesProjectBasename(f, projectBasename) {
    return (projectBasename !== undefined && getBasenameWithoutExtension(f.originalFileName).toLowerCase() === projectBasename);
}
function getBasenameWithoutExtension(fileName) {
    const lastSlashIndex = fileName.lastIndexOf('/');
    const base = lastSlashIndex >= 0 ? fileName.substring(lastSlashIndex + 1) : fileName;
    const lastDotIndex = base.lastIndexOf('.');
    return lastDotIndex >= 0 ? base.substring(0, lastDotIndex) : base;
}
function pathDepth(fileName) {
    return fileName.split('/').length - 1;
}
function getSchematicFromResult(result) {
    if (result.skipReason) {
        return undefined;
    }
    if (!result.metadata[constants_1.KicadNetlistSchematicModel]) {
        return undefined;
    }
    return result.metadata[constants_1.KicadNetlistSchematicModel];
}
//# sourceMappingURL=kicadNetlistValidation.js.map